<template>
  <modal :front="true" :opened="opened" no-pad="true">
    <div ref="conteudo" class="height-95 pt-3">
      <loading v-if="consultandoSolicitacoes && !consultaSilenciosa" class="py-4" text="Consultando solicitação..."/>
      <div v-else-if="erroConsulta" class="container-fluid pb-3">
        <div class="text-center mb-3 p-3" role="alert">
          <div class="font-weight-bold mb-2">
            <i aria-hidden="true" class="fa fa-exclamation-triangle text-warning mr-2"></i>{{ erroNaCotacao ? 'Não foi possível cotar a entrega' : 'Não foi possível consultar a solicitação' }}
          </div>
          <div>{{ erroConsulta }}</div>
          <button class="btn btn-primary btn-sm mt-3" @click="tentarNovamente">
            Tentar novamente
          </button>
        </div>
      </div>
      <div v-else-if="solicitacoes.length" class="container-fluid pb-3">
        <h6 class="font-weight-bold mb-0 text-center">Status da solicitação</h6>
        <hr>
        <p v-if="erroSolicitacao" class="alert alert-warning">{{ erroSolicitacao }}</p>
        <div v-for="solicitacao in solicitacoes" :key="solicitacao.id_logistica" class="mb-4">
          <div class="text-center mb-3 p-3" role="status">
            <div>
              <div class="font-weight-bold mb-2">
                <i :class="infoStatus(solicitacao.status_logistica).icone" aria-hidden="true" class="fa mr-2"></i>{{ infoStatus(solicitacao.status_logistica).titulo }}
              </div>
              <div>{{ infoStatus(solicitacao.status_logistica).descricao }}</div>
              <p v-if="solicitacao.reconciliacao === 'nao_encontrado'" class="alert alert-warning">A solicitação ainda não foi encontrada na Marcha. Aguardando nova consulta para confirmar a criação.</p>
          <div v-if="solicitacao.status_logistica === 'pagamento_pendente' && expiracaoPix(solicitacao.pix)" class="small mt-1">
                Válido até {{ expiracaoPix(solicitacao.pix) }}
              </div>
            </div>
          </div>
          <div v-if="solicitacao.status_logistica === 'pagamento_pendente' && solicitacao.pix" class="text-center">
            <img v-if="qrcodes[solicitacao.id_logistica]" :src="qrcodes[solicitacao.id_logistica]" alt="QR Code PIX para pagamento da entrega" class="pix-qrcode">
            <p v-else class="alert alert-warning">QR Code indisponível. Use o código PIX abaixo.</p>
            <textarea v-if="solicitacao.pix.qr_code_text" :value="solicitacao.pix.qr_code_text" aria-label="Código PIX copia e cola" class="form-control mt-3 mb-3" readonly rows="3" @focus="$event.target.select()"></textarea>
          </div>
          <div class="text-center">
            <button :disabled="consultandoSolicitacoes || solicitando || !!cancelando" class="btn btn-link btn-sm text-info" @click="consultarSolicitacoes">
              <i :class="{'fa-spin': consultandoSolicitacoes && !consultaSilenciosa}" class="fa fa-sync-alt mr-1"></i> {{ consultandoSolicitacoes && !consultaSilenciosa ? 'Atualizando...' : 'Atualizar status' }}
            </button>
          </div>
          <p v-if="solicitacao.mensagem_erro" class="alert alert-warning">{{ solicitacao.mensagem_erro }}</p>
          <a v-if="urlRastreamento(solicitacao.tracking_url)" :href="urlRastreamento(solicitacao.tracking_url)" class="btn btn-outline-primary btn-block" rel="noopener noreferrer" target="_blank">Acompanhar entrega</a>
        </div>
      </div>
      <div v-else-if="plataforma" class="container-fluid pb-2">
        <loading v-if="cotando || solicitando" :text="solicitando ? 'Solicitando entregador...' : 'Consultando cotação...'" class="py-4"/>
        <div v-else-if="cotacao">
          <h6 class="font-weight-bold mb-2 text-center">Cotação da Entrega</h6>
          <div v-if="formasPagamento.length">
            <label class="mb-1" for="pagamentoCotacao">Pagamento da entrega</label>
            <tooltip class="ml-1" msg="Forma de pagamento à plataforma de integração pelo serviço de entrega. Não é o pagamento do valor do pedido feito pelo cliente."/>
            <select id="pagamentoCotacao" v-model="formaPagamento" class="form-control" required @change="cotar">
              <option disabled value="">Selecione a forma de pagamento</option>
              <option v-for="forma in formasPagamento" :key="forma.id" :value="forma.id">{{ forma.name }}</option>
            </select>
          </div>
          <div v-if="retornoDisponivel" class="mt-2">
            <label class="mb-1" for="retornoCotacao">Retornar à loja</label>
            <tooltip class="ml-1" msg="O entregador retorna à loja para entregar dinheiro, maquininha ou outros itens após as entregas."/>
            <select id="retornoCotacao" v-model="retornoObrigatorio" class="form-control" @change="cotar">
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>
          <ul class="list-group mb-3 mt-3">
            <li class="list-group-item d-flex justify-content-between align-items-center">
              <span>Entrega</span><strong>{{ moeda(cotacao.pricing.delivery_value) }}</strong>
            </li>
            <li v-if="cotacao.pricing.transaction_fee !== null" class="list-group-item d-flex justify-content-between align-items-center">
              <span>Taxa</span><strong>{{ moeda(cotacao.pricing.transaction_fee) }}</strong>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center">
              <span><i class="fa fa-route text-muted mr-2"></i> Distância</span><strong>{{ cotacao.distance_km }} km</strong>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center">
              <span><i class="fa fa-clock text-muted mr-2"></i> Previsão</span><strong>{{ previsao }} minutos</strong>
            </li>
            <li class="list-group-item list-group-item-light d-flex justify-content-between align-items-center">
              <strong>Total</strong><strong class="text-primary">{{ moeda(cotacao.pricing.total_value) }}</strong>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="border-top p-2 text-center d-flex justify-content-between">
      <button :disabled="solicitando || !!cancelando" class="btn btn-dark border p-3" @click="voltar()"><i class="fa fa-angle-left mr-1"></i> Voltar</button>
      <button v-if="!solicitacoes.length && plataforma && cotacao && !cotando && !consultandoSolicitacoes && !erroConsulta" :disabled="solicitando"
              class="btn btn-success p-3"
              @click="solicitar">
        Solicitar <i class="fa fa-motorcycle ml-1"></i>
      </button>
      <div v-if="solicitacoesCancelaveis.length" class="d-flex flex-wrap justify-content-end">
        <button v-for="solicitacao in solicitacoesCancelaveis" :key="solicitacao.id_logistica"
                :disabled="!!cancelando" class="btn btn-outline-danger p-3 ml-2" @click="cancelar(solicitacao)">
          <i class="fa fa-trash mr-1"></i> {{ cancelando === solicitacao.id_logistica ? 'Cancelando...' : 'Cancelar' }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from "@/components/gestor/ModalGestor";
import Loading from "@/components/gestor/loading";
import Tooltip from "@/components/commons/Tooltip";

export default {
  name: "ModalLogistica",
  components: {Modal, Loading, Tooltip},
  props: {
    opened: Boolean,
    despachar: Boolean,
    pedidos: { type: Array, default: () => [] },
    plataformaSelecionada: { default: null },
    idRota: { default: null },
    idLogistica: { default: null },
    concluirDespacho: Boolean
  },
  data() {
    return { plataforma: null, cotacao: null, cotando: false, consulta: 0, formasPagamento: [], formaPagamento: '', retornoObrigatorio: null, solicitacoes: [], qrcodes: {}, consultandoSolicitacoes: false, consultaSilenciosa: false, erroConsulta: '', erroNaCotacao: false, solicitando: false, erroSolicitacao: '', atualizacaoPendente: false, cancelando: null };
  },
  computed: {
    retornoDisponivel() {
      return typeof this.cotacao?.return_required === 'boolean';
    },
    solicitacoesCancelaveis() { return this.solicitacoes.filter(solicitacao => this.podeCancelar(solicitacao)); },
    pagamentoValido() {
      return !(this.cotacao && this.cotacao.payment_required) || this.formasPagamento.some(forma => forma.id === this.formaPagamento);
    },
    previsao() {
      const tempo = this.cotacao.estimated_time;
      return tempo.min_minutes === tempo.max_minutes ? tempo.min_minutes : `${tempo.min_minutes} a ${tempo.max_minutes}`;
    }
  },
  watch: {
    opened(aberto) {
      this.consulta++;
      this.plataforma = this.plataformaSelecionada;
      this.solicitacoes = [];
      this.qrcodes = {};
      this.erroConsulta = '';
      this.erroNaCotacao = false;
      this.erroSolicitacao = '';
      this.cotacao = null;
      this.formasPagamento = [];
      this.formaPagamento = '';
      this.retornoObrigatorio = null;
      this.consultandoSolicitacoes = false;
      this.consultaSilenciosa = false;
      this.atualizacaoPendente = false;
      this.cotando = false;
      if (!aberto) return;
      if (this.idLogistica || (this.pedidos.length === 1 && Number(this.pedidos[0].id_auth_entregador))) this.consultarSolicitacoes();
      else this.cotar();
    }
  },
  beforeDestroy() { this.consulta++; },
  sockets: {
    connect() {
      this.solicitarAtualizacao();
    },
    logistics_status(data) {
      if (!this.opened || !data) return;
      const mesmaLogistica = data.id_logistica && (Number(data.id_logistica) === Number(this.idLogistica)
        || this.solicitacoes.some(item => Number(item.id_logistica) === Number(data.id_logistica)));
      if (mesmaLogistica) this.solicitarAtualizacao();
    }
  },
  methods: {
    expiracaoPix(pix) {
      if (!pix || !pix.expiracao) return '';
      const expiracao = this.$dayjs(pix.expiracao);
      return expiracao.isValid() ? expiracao.format('DD/MM/YYYY [às] HH:mm:ss') : '';
    },
    podeCancelar(solicitacao) {
      return Number(solicitacao.status) === 1 && !!solicitacao.external_order_id && solicitacao.codigo_erro !== 'IFOOD_CANCELLATION_PENDING';
    },
    async cancelar(solicitacao) {
      if (this.cancelando || !this.podeCancelar(solicitacao)) return;
      this.cancelando = solicitacao.id_logistica;
      const consulta = ++this.consulta;
      this.atualizacaoPendente = false;
      try {
        this.erroSolicitacao = '';
        const reasonsResponse = await this.$http.post('v2/integracoes/logistica/motivos-cancelamento', { id_logistica: solicitacao.id_logistica }, { emulateJSON: false });
        if (consulta !== this.consulta) return;
        const reasons = reasonsResponse.data;
        if (!reasons || typeof reasons.exige_motivo !== 'boolean' || !Array.isArray(reasons.motivos)) throw new Error('Resposta inválida ao consultar motivos.');
        const payload = { id_logistica: solicitacao.id_logistica };
        let confirmado;
        if (reasons.exige_motivo) {
          if (!reasons.motivos.length) throw new Error('O iFood não permite cancelar esta entrega no momento.');
          const content = document.createElement('div');
          const select = document.createElement('select');
          select.className = 'form-control mb-3';
          reasons.motivos.forEach(motivo => {
            const option = document.createElement('option');
            option.value = motivo.codigo;
            option.textContent = motivo.descricao;
            select.appendChild(option);
          });
          const input = document.createElement('textarea');
          input.className = 'form-control';
          input.placeholder = 'Justificativa do cancelamento (obrigatória)';
          content.appendChild(select);
          content.appendChild(input);
          confirmado = await this.$swal({ title: 'Cancelar entrega', text: 'A entrega no iFood será cancelada. O pedido no LeCard será mantido.', content, buttons: ['Voltar', 'Solicitar cancelamento'], dangerMode: true });
          payload.codigo_motivo = Number(select.value);
          payload.justificativa = input.value.trim();
          if (confirmado && !payload.justificativa) throw new Error('Informe a justificativa do cancelamento.');
        } else {
          confirmado = await this.$swal({ text: 'Deseja cancelar esta solicitação de entrega?', buttons: ['Não', 'Sim'], dangerMode: true });
        }
        if (!confirmado || consulta !== this.consulta) return;
        this.erroSolicitacao = '';
        const response = await this.$http.post('v2/integracoes/logistica/cancelar', payload, { emulateJSON: false });
        if (consulta !== this.consulta) return;
        if (response.data && Number(response.data.id_logistica) === Number(solicitacao.id_logistica) && response.data.cancelamento_pendente) {
          Object.assign(solicitacao, response.data);
          this.$swal('Cancelamento solicitado. Aguarde a confirmação do iFood.');
          return;
        }
        if (!response.data || Number(response.data.id_logistica) !== Number(solicitacao.id_logistica) || response.data.status_logistica !== 'cancelada') {
          throw new Error('Não foi possível confirmar o cancelamento da solicitação.');
        }
        this.$emit('atualizar', { ids_pedidos: response.data.ids_pedidos, entregador: null, despachar: false, status_entrega: null });
        this.cancelando = null;
        this.fechar();
        this.$swal('Entregador cancelado com sucesso!');
      } catch (erro) {
        if (consulta === this.consulta) this.erroSolicitacao = erro.data && erro.data.msg || erro.message || 'Erro ao cancelar solicitação.';
      } finally {
        this.cancelando = null;
      }
    },
    idsPedidos() {
      const ids = this.pedidos.map(pedido => Number(pedido.id_pedido));
      if (!ids.length || ids.some(id => !Number.isSafeInteger(id) || id <= 0)) throw new Error('Informe pedidos válidos para consultar a entrega.');
      return ids;
    },
    payloadSolicitacao() {
      const payload = { id_auth: this.plataforma.id_auth, ids_pedidos: this.idsPedidos() };
      if (typeof this.retornoObrigatorio === 'boolean') payload.return_required = this.retornoObrigatorio;
      if (this.idRota) {
        payload.id_rota = Number(this.idRota);
        payload.id_entregador = Number(this.plataforma.id_usuario);
      }
      if (this.formaPagamento) {
        payload.payment_method_id = this.formaPagamento;
        const forma = this.formasPagamento.find(item => item.id === this.formaPagamento);
        if (forma && forma.type) payload.payment_method_type = forma.type;
      }
      return payload;
    },
    infoStatus(status) {
      return {
        enviando: { titulo: 'Enviando solicitação', descricao: 'Aguardando a confirmação da plataforma de entrega.', icone: 'fa-paper-plane', cor: 'info' },
        recusada: { titulo: 'Solicitação recusada', descricao: 'A plataforma recusou a solicitação de entrega.', icone: 'fa-exclamation-circle', cor: 'danger' },
        pagamento_pendente: { titulo: 'Aguardando pagamento', descricao: 'Pague a entrega para continuar a solicitação.', icone: 'fa-wallet', cor: 'warning' },
        aguardando_entregador: { titulo: 'Aguardando Entregador', descricao: 'Aguardando um entregador aceitar a solicitação.', icone: 'fa-bell', cor: 'info' },
        entregador_aceitou: { titulo: 'Entregador aceitou', descricao: 'Um entregador aceitou a solicitação de entrega.', icone: 'fa-user-check', cor: 'success' },
        aguardando_coleta: { titulo: 'Aguardando coleta', descricao: 'Aguardando o entregador retirar o pedido.', icone: 'fa-box', cor: 'info' },
        entregando: { titulo: 'Em entrega', descricao: 'O entregador está a caminho do destino.', icone: 'fa-motorcycle', cor: 'info' },
        finalizada: { titulo: 'Entrega finalizada', descricao: 'A entrega foi concluída.', icone: 'fa-check-circle', cor: 'success' },
        cancelada: { titulo: 'Entrega cancelada', descricao: 'A solicitação de entrega foi cancelada.', icone: 'fa-times-circle', cor: 'secondary' }
      }[status] || { titulo: 'Aguardando atualização', descricao: 'Aguardando informações sobre a solicitação de entrega.', icone: 'fa-clock', cor: 'info' };
    },
    urlRastreamento(url) {
      try { const parsed = new URL(url); return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null; } catch (_) { return null; }
    },
    solicitarAtualizacao() {
      if (!this.opened || this.cancelando) return;
      if (this.consultandoSolicitacoes || this.solicitando) {
        this.atualizacaoPendente = true;
        return;
      }
      if (!this.idLogistica && !this.solicitacoes.length) return;
      this.atualizacaoPendente = false;
      this.consultarSolicitacoes(true);
    },
    async exibirSolicitacoes(solicitacoes, consulta) {
      if (consulta !== this.consulta) return;
      this.solicitacoes = solicitacoes;
      const solicitacaoRota = solicitacoes.find(item => Number(item.id_rota) === Number(this.idRota))
        || (solicitacoes.length === 1 && !solicitacoes[0].id_rota ? solicitacoes[0] : null);
      if (this.idRota && solicitacaoRota) {
        this.$emit('status-atualizado', { id_rota: this.idRota, status_entrega: solicitacaoRota.status_entrega });
      }
      this.cotacao = null;
      const qrcodes = {};
      solicitacoes.forEach(item => {
        const qr = item.pix && item.pix.qr_code;
        if (typeof qr !== 'string' || !qr.trim()) return;
        if (/^data:image\/(png);base64,[A-Za-z0-9+/=\s]+$/.test(qr)) qrcodes[item.id_logistica] = qr;
      });
      if (consulta !== this.consulta) return;
      this.qrcodes = qrcodes;

    },
    async consultarSolicitacoes(silenciosa = false) {
      if (this.consultandoSolicitacoes || this.cancelando) return;
      const idLogistica = this.idLogistica || (this.solicitacoes[0] && this.solicitacoes[0].id_logistica);
      if (!this.pedidos.length && !idLogistica) return;
      const consulta = ++this.consulta;
      this.consultaSilenciosa = silenciosa === true && this.solicitacoes.length > 0;
      this.consultandoSolicitacoes = true;
      this.erroConsulta = '';
      try {
        const response = await this.$http.post('v2/integracoes/logistica/consultar', idLogistica ? { id_logistica: Number(idLogistica) } : { id_pedido: this.idsPedidos()[0] }, { emulateJSON: false });
        if (consulta !== this.consulta) return;
        if (!response.data || !response.data.id_logistica) throw new Error('Resposta inválida ao consultar solicitação.');
        await this.exibirSolicitacoes([response.data], consulta);
      } catch (erro) {
        if (consulta === this.consulta) {
          const mensagem = erro.data && erro.data.msg || erro.message || 'Erro ao consultar solicitação.';
          if (this.consultaSilenciosa) this.erroSolicitacao = mensagem;
          else {
            this.erroNaCotacao = false;
            this.erroConsulta = mensagem;
          }
        }
      } finally {
        if (consulta === this.consulta) {
          this.consultandoSolicitacoes = false;
          this.consultaSilenciosa = false;
          if (this.atualizacaoPendente) this.solicitarAtualizacao();
        }
      }
    },
    async solicitar() {
      if (this.solicitando || !this.cotacao || this.solicitacoes.length) return;
      if (!this.pagamentoValido) {
        this.$swal('Selecione o pagamento da entrega para solicitar o entregador.');
        return;
      }
      const consulta = ++this.consulta;
      this.solicitando = true;
      this.erroSolicitacao = '';
      try {
        const response = await this.$http.post('v2/integracoes/logistica/solicitar', { ...this.payloadSolicitacao(), despachar: this.idRota ? !!this.concluirDespacho : this.despachar }, { emulateJSON: false });
        if (consulta !== this.consulta) return;
        if (!response.data || response.data.success === false) throw new Error(response.data && response.data.msg || 'Erro ao solicitar entregador.');
        const solicitacoes = response.data.id_logistica ? [response.data] : [];
        if (!solicitacoes.length) throw new Error('Resposta inválida ao solicitar entregador.');
        await this.exibirSolicitacoes(solicitacoes, consulta);
        if (consulta === this.consulta) {
          this.$emit('atualizar', {
            ids_pedidos: response.data.ids_pedidos,
            entregador: ['cancelada', 'recusada'].includes(response.data.status_logistica) ? null : this.plataforma,
            despachar: this.idRota ? !!this.concluirDespacho : this.despachar,
            id_logistica: response.data.id_logistica,
            status_logistica: response.data.status_logistica,
            status_entrega: response.data.status_entrega,
            rota_despachada: !!response.data.rota_despachada
          });
          if (response.data.erro_despacho) this.erroSolicitacao = response.data.erro_despacho;
        }
      } catch (erro) {
        if (consulta !== this.consulta) return;
        this.$swal(erro.data && erro.data.msg || erro.message || 'Erro ao solicitar entregador.');
      } finally {
        this.solicitando = false;
        if (consulta === this.consulta && this.atualizacaoPendente) this.solicitarAtualizacao();
      }
    },
    moeda(valor) { return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); },
    voltar() { this.fechar(); },
    tentarNovamente() {
      if (this.erroNaCotacao) this.cotar();
      else this.consultarSolicitacoes();
    },
    async cotar() {
      const consulta = ++this.consulta;
      this.cotando = true;
      this.erroConsulta = '';
      this.cotacao = null;
      try {
        const payload = this.payloadSolicitacao();
        const response = await this.$http.post('v2/integracoes/logistica/cotar', payload, { emulateJSON: false });
        if (consulta !== this.consulta) return;
        if (!response.data || !response.data.pricing) throw new Error(response.data && response.data.msg || 'Não foi possível consultar a cotação.');
        if (Array.isArray(response.data.payment_methods)) this.formasPagamento = response.data.payment_methods;
        if (response.data.payment_method_id && this.formasPagamento.some(forma => forma.id === response.data.payment_method_id)) {
          this.formaPagamento = response.data.payment_method_id;
        }
        this.retornoObrigatorio = typeof response.data.return_required === 'boolean' ? response.data.return_required : null;
        this.cotacao = response.data;
      } catch (erro) {
        if (consulta !== this.consulta) return;
        if (this.pedidos.length === 1 && Number(erro.status) === 409 && erro.data && erro.data.code === 'LOGISTICS_REQUEST_ALREADY_EXISTS') {
          this.cotando = false;
          await this.consultarSolicitacoes();
          return;
        }
        this.erroNaCotacao = true;
        this.erroConsulta = erro.data && erro.data.msg || erro.message || 'Erro ao consultar cotação.';
      } finally {
        if (consulta === this.consulta) this.cotando = false;
      }
    },
    fechar() {
      if (this.solicitando || this.cancelando) return;
      this.consulta++;
      this.$emit('fechar');
    },

  }
};
</script>

<style scoped>
.pix-qrcode { display: block; width: 200px; max-width: 100%; height: auto; margin: auto; }
</style>
